import { ext, mat3 } from '@antv/matrix-util';

import { colors } from 'ui/theme';
import { GraphMode } from '../../constants';
import { Graph, Shape } from '../../types';
import { groupIds, shapeNames } from '../constants';

const { transform } = ext;

const shapeAttrs = {
  purple: { fill: colors.purple100, opacity: 0.05 },
  grey: { fill: colors.grey90, opacity: 0.2 },
};

interface GetOverlayShapeAttrs {
  x: number;
  y: number;
  width: number;
  height: number;
}

const getOverlayShapeAttrs = (graph: Graph): GetOverlayShapeAttrs => {
  const { width, height } = graph.get('canvas').get('el');
  return { x: 0, y: 0, width, height };
};

export const drawOverlay = (graph: Graph) => {
  const rootGroup = graph.getGroup();
  const overlayGroup = rootGroup.addGroup({ id: groupIds.overlay, visible: false });
  const attrs = getOverlayShapeAttrs(graph);

  overlayGroup.addShape('rect', {
    name: shapeNames.overlayPurple,
    className: shapeNames.overlayPurple,
    attrs: {
      ...attrs,
      ...shapeAttrs.purple,
    },
  });

  overlayGroup.addShape('rect', {
    name: shapeNames.overlayGrey,
    className: shapeNames.overlayGrey,
    attrs: {
      ...attrs,
      ...shapeAttrs.grey,
    },
  });

  // Sets the original matrix.  Needed for future transforms.
  overlayGroup.setMatrix(overlayGroup.getMatrix() || mat3.create());

  return overlayGroup;
};

const getOverlay = (graph: Graph) => {
  const children = graph.getGroup().getChildren();
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child?.get('id') === groupIds.overlay) {
      return child;
    }
  }
};

/**
 * Moves the overlay so that it will cover 100% of the viewable graph
 */
export const fixOverlayDimensions = (graph: Graph) => {
  const overlayGroup = getOverlay(graph);

  if (overlayGroup) {
    const { width, height } = graph.get('canvas').get('el');

    // Clear the matrix to reset all the transformantions on the shape or group.
    overlayGroup.resetMatrix();

    // Resize the group container to fit the current canvas size
    overlayGroup.attr('width', width);
    overlayGroup.attr('height', height);

    // Resize each of the children to fit the current canvas size
    overlayGroup.get('children').forEach((g: Shape) => {
      g.attr('width', width);
      g.attr('height', height);
    });

    // Transform viewport coordinates into canvas coordinates.
    const { x, y } = graph.getPointByCanvas(0, 0);
    // Zoom, converted to ratio for scaling
    const zoomRatio = 1 / graph.getZoom();
    // Original matrix
    const matrix = overlayGroup.getMatrix();
    // Matrix which will fit to 100% of the current graph view
    const newMatrix = transform(matrix, [
      // scale to current zoom
      ['s', zoomRatio, zoomRatio],
      // translate to current top left point on canvas
      ['t', x, y],
    ]);

    overlayGroup.setMatrix(newMatrix);
  }
};

export const setOverlayVisibility = (
  graph: Graph,
  isOverlayVisible = false,
  mode: GraphMode = GraphMode.Default,
) => {
  if (!graph) return;

  graph.set('isOverlayVisible', isOverlayVisible);

  const group = graph.getGroup();
  const overlayGroup = group.findById(groupIds.overlay) || drawOverlay(graph);

  if (isOverlayVisible) {
    graph.setMode(GraphMode.OverlayVisible);
    overlayGroup.show();
    overlayGroup.toFront();
    fixOverlayDimensions(graph);
    graph.findAllByState('node', 'selected').forEach((node) => node.toFront());
  } else {
    graph.setMode(mode);
    overlayGroup.hide();
  }
};
